import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import Axios from 'axios';

import { onUpdatePlaylists } from '@hooks/API/Contact/UseContactPlaylists';

import {
    getFastPackageSourceKeyFromPlaylists,
    normalizeFastPackageBooks,
} from './modelDetailsBooks.utils';

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

export function useFastPackageOrderManager({ playlists, contactID, enabled, hasAnyFastPackage }) {
    const [fastPackageOnly, setFastPackageOnly] = useState(false);
    const [fastPackageDragBooks, setFastPackageDragBooks] = useState([]);
    const [savingFastPackageOrder, setSavingFastPackageOrder] = useState(false);
    const [saveOrderFeedback, setSaveOrderFeedback] = useState(null);

    const normalizedFastPackageBooks = useMemo(() => {
        if (!enabled || !fastPackageOnly || !playlists) {
            return [];
        }

        return normalizeFastPackageBooks([...playlists]);
    }, [enabled, fastPackageOnly, playlists]);

    const fastPackageSourceKey = useMemo(() => {
        if (!enabled || !fastPackageOnly || !playlists) {
            return '';
        }

        return getFastPackageSourceKeyFromPlaylists(playlists);
    }, [enabled, fastPackageOnly, playlists]);

    const displayedFastPackageBooks = useMemo(() => {
        if (!fastPackageOnly) {
            return [];
        }

        return fastPackageDragBooks.length > 0 ? fastPackageDragBooks : normalizedFastPackageBooks;
    }, [fastPackageDragBooks, fastPackageOnly, normalizedFastPackageBooks]);

    useEffect(() => {
        if (!saveOrderFeedback) {
            return undefined;
        }

        const timer = window.setTimeout(() => setSaveOrderFeedback(null), 5000);
        return () => window.clearTimeout(timer);
    }, [saveOrderFeedback]);

    useEffect(() => {
        if (!enabled || !hasAnyFastPackage) {
            setFastPackageOnly(false);
        }
    }, [enabled, hasAnyFastPackage]);

    useEffect(() => {
        if (!enabled || !fastPackageOnly) {
            setSaveOrderFeedback(null);
        }
    }, [enabled, fastPackageOnly]);

    useLayoutEffect(() => {
        if (!enabled || !fastPackageOnly) {
            return;
        }

        setFastPackageDragBooks(normalizedFastPackageBooks.map((book) => ({ ...book })));
    }, [enabled, fastPackageOnly, fastPackageSourceKey, normalizedFastPackageBooks]);

    const handleFastPackageDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        setFastPackageDragBooks((previousBooks) => {
            const source =
                previousBooks.length > 0
                    ? previousBooks
                    : normalizedFastPackageBooks.map((book) => ({ ...book }));
            const reorderedBooks = reorder(source, result.source.index, result.destination.index);

            return reorderedBooks.map((book, index) => ({
                ...book,
                FastPackageOrder: index,
            }));
        });
    };

    const handleSaveFastPackageOrder = async () => {
        if (!enabled || !fastPackageOnly || contactID == null || !displayedFastPackageBooks.length) {
            return;
        }

        setSavingFastPackageOrder(true);
        try {
            await Axios.post('/playlists/setFastPackagesOrder', {
                ModelID: Number(contactID),
                FastPackageIDs: displayedFastPackageBooks.map((book) => Number(book.ID)),
            });
            onUpdatePlaylists(contactID);
            setSaveOrderFeedback({ type: 'success', text: 'Fast package order saved.' });
        } catch (error) {
            console.error('error saving fast package order', error);
            setSaveOrderFeedback({ type: 'error', text: 'Could not save fast package order.' });
        } finally {
            setSavingFastPackageOrder(false);
        }
    };

    return {
        displayedFastPackageBooks,
        fastPackageOnly,
        handleFastPackageDragEnd,
        handleSaveFastPackageOrder,
        hasAnyFastPackage,
        saveOrderFeedback,
        savingFastPackageOrder,
        setFastPackageOnly,
        setSaveOrderFeedback,
    };
}
